import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search as SearchIcon, ShieldCheck, IndianRupee, Fingerprint, Compass, ArrowRight, Building2 } from "lucide-react";
import { api, PROPERTY_TYPES, FEATURED_CITIES, CITY_STATE } from "@/lib/loop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PropertyCard from "@/components/PropertyCard";
import Footer from "@/components/Footer";

const HERO = "https://images.unsplash.com/photo-1769271972348-429a8ad18f51?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const TRUST = [
  { Icon: ShieldCheck, h: "Verified Properties", t: "Properties undergo defined verification checks." },
  { Icon: IndianRupee, h: "Transparent Pricing", t: "See asking price and disclosed additional costs." },
  { Icon: Fingerprint, h: "Property Passport", t: "Every property gets a unique digital identity." },
  { Icon: Compass, h: "Real-World Intelligence", t: "Understand the property and its surrounding location." },
];

const STEPS = [
  { n: "01", h: "DISCOVER", t: "Find properties based on your actual requirements." },
  { n: "02", h: "VERIFY", t: "Review property identity, availability, documents and location information." },
  { n: "03", h: "DECIDE", t: "Compare properties and arrange a site visit." },
];

export default function Home() {
  const nav = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState("");
  const [ptype, setPtype] = useState("");
  const [listing, setListing] = useState("buy");

  useEffect(() => {
    api.get("/properties/featured").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  const doSearch = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (ptype) params.set("property_type", ptype);
    if (listing) params.set("listing_type", listing);
    nav(`/search?${params.toString()}`);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center">
        <img src={HERO} alt="City" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full py-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#E6C687] font-semibold border border-[#C5A059]/40 rounded-full px-4 py-1.5 mb-6 backdrop-blur-sm">
              <ShieldCheck className="w-3.5 h-3.5" /> Now across India · Mumbai · Delhi · Bengaluru · +more
            </div>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.05]">
              REAL ESTATE,<br />
              <span className="font-serif-display italic font-medium text-[#E6C687]">Verified.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
              Discover properties with transparent pricing, verification status, documents and real-world location intelligence.
            </p>

            {/* Search */}
            <div className="mt-8 bg-[#0B132B]/85 backdrop-blur-xl border border-white/10 rounded-2xl p-3 shadow-2xl">
              <div className="flex gap-1 mb-3">
                {["buy", "rent"].map((l) => (
                  <button key={l} data-testid={`hero-tab-${l}`} onClick={() => setListing(l)}
                    className={`px-5 py-1.5 rounded-lg text-sm font-semibold capitalize transition-colors
                      ${listing === l ? "bg-[#C5A059] text-[#070C1E]" : "text-slate-300 hover:text-white"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 flex items-center gap-2 bg-[#131B35] rounded-xl px-3">
                  <SearchIcon className="w-4 h-4 text-slate-500" />
                  <Input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                    placeholder="Locality, project or Property ID (e.g. Patia, LP-BBSR-...)"
                    className="border-0 bg-transparent text-white focus-visible:ring-0 px-0" />
                </div>
                <Select value={ptype} onValueChange={setPtype}>
                  <SelectTrigger data-testid="hero-type-select" className="sm:w-44 bg-[#131B35] border-white/10 text-white">
                    <SelectValue placeholder="Property type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="hero-search-btn" onClick={doSearch}
                  className="bg-[#C5A059] hover:bg-[#b18f47] text-[#070C1E] font-semibold px-8">
                  Search
                </Button>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button data-testid="hero-explore-btn" onClick={() => nav("/search")}
                className="bg-white text-[#070C1E] hover:bg-slate-200 font-semibold">
                Explore Verified Properties <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button data-testid="hero-list-btn" onClick={() => nav("/owner")} variant="ghost"
                className="text-white border border-white/25 hover:bg-white/10">
                List Your Property
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 -mt-16 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TRUST.map((t, i) => (
            <motion.div key={t.h} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-[#0B132B] border border-white/10 rounded-2xl p-6 hover:border-[#C5A059]/30 transition-colors">
              <t.Icon className="w-7 h-7 text-[#C5A059]" />
              <h3 className="mt-4 text-base font-bold text-white">{t.h}</h3>
              <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{t.t}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold">Featured</div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-white tracking-tight">Verified Properties</h2>
          </div>
          <Button data-testid="view-all-btn" onClick={() => nav("/search")} variant="ghost"
            className="hidden sm:flex text-[#C5A059] hover:text-[#E6C687] hover:bg-transparent">
            View all <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p, i) => <PropertyCard key={p.id} p={p} index={i} testid={`featured-card-${i}`} />)}
        </div>
      </section>

      {/* HOW LOOP WORKS */}
      <section className="bg-[#0B132B] border-y border-white/10 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight max-w-2xl">
            Less noise. More information. <span className="text-[#C5A059]">Better property decisions.</span>
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="font-serif-display text-5xl text-[#C5A059]/40 font-semibold">{s.n}</div>
                <h3 className="mt-3 text-xl font-bold text-white tracking-wide">{s.h}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.t}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PASSPORT EXPLAINER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold">The signature feature</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-white tracking-tight">Every property gets a Property Passport</h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              A unique digital identity for each listing — its verification status, owner status, document trail,
              date-stamped media and a full timeline. We answer not just <em>"what's available?"</em> but
              <span className="text-white"> "what do I actually know about this property?"</span>
            </p>
            <ul className="mt-6 space-y-3">
              {["Unique LOOP Property ID", "Verification timeline you can audit", "Document status without false 'clear title' claims", "Date-stamped photos to kill misleading old images"].map((li) => (
                <li key={li} className="flex items-start gap-3 text-sm text-slate-300">
                  <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {li}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#070C1E] border border-[#C5A059]/30 rounded-3xl p-8 watermark-grid">
            <div className="font-mono-loop text-sm tracking-widest text-[#E6C687] bg-[#C5A059]/10 inline-block px-3 py-1.5 rounded-md border border-[#C5A059]/30">
              LP-BBSR-PAT-000124
            </div>
            <div className="mt-6 space-y-4">
              {[["Property Status", "Available", "text-white"],
                ["Owner Status", "Identity Verified", "text-emerald-400"],
                ["Location", "Verified", "text-emerald-400"],
                ["Documents", "4 of 7 supplied", "text-amber-400"]].map(([k, v, c]) => (
                <div key={k} className="flex justify-between border-b border-white/5 pb-3">
                  <span className="text-xs uppercase tracking-wider text-slate-500">{k}</span>
                  <span className={`text-sm font-semibold ${c}`}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-20">
        <h2 className="text-2xl font-bold text-white mb-8">Explore by city</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURED_CITIES.map((c) => (
            <button key={c} data-testid={`city-${c}`} onClick={() => nav(`/search?city=${c}`)}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0B132B] p-6 text-left hover:border-[#C5A059]/40 transition-colors">
              <Building2 className="w-6 h-6 text-[#C5A059]" />
              <div className="mt-6 text-lg font-bold text-white">{c}</div>
              <div className="text-xs text-slate-500">{CITY_STATE[c]}</div>
              <ArrowRight className="absolute top-6 right-6 w-4 h-4 text-slate-600 group-hover:text-[#C5A059] group-hover:translate-x-1 transition-all" />
            </button>
          ))}
        </div>
      </section>

      {/* OWNER CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-24">
        <div className="rounded-3xl bg-gradient-to-br from-[#1C2541] to-[#070C1E] border border-[#C5A059]/20 p-10 sm:p-14 text-center gold-glow">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">List your property on LOOP</h2>
          <p className="mt-3 text-slate-400 max-w-xl mx-auto">Give your asset a professional Property Passport. Reach serious, verified buyers — not broker spam.</p>
          <Button data-testid="cta-list-btn" onClick={() => nav("/owner")}
            className="mt-6 bg-[#C5A059] hover:bg-[#b18f47] text-[#070C1E] font-semibold px-8">
            List Your Property <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
